import { DateTime } from 'luxon';
import { v4 as uuid } from 'uuid';

import {
  TimeAvailabilityFeatureProperties,
  WeeklyAvailability,
} from 'lane-shared/types/features/TimeAvailabilityFeatureProperties';

import { WEEK_DAYS } from './helpers/getUnavailableDatesAndWeekDays';
import { mapWeeklyAvailabilitiesToOperatingHours } from './mapWeeklyAvailabilitiesToOperatingHours';

export function getWeeklyAvailabilitiesByMembership(
  availabilities: TimeAvailabilityFeatureProperties['availabilities'],
  base62GroupRoleIds: string[]
) {
  return availabilities.filter(wa => {
    if (!wa.groupRole?._id) {
      return false;
    }

    return base62GroupRoleIds.includes(wa.groupRole._id);
  });
}

// Creates a fallback default availability that is available 24/7
// This is used when no availability with allGroupRoles=true is found
function createFallbackDefaultAvailability(): WeeklyAvailability {
  const weekTimeRules: any = {
    _id: uuid(),
  };

  Object.values(WEEK_DAYS).forEach(day => {
    weekTimeRules[day] = {
      _id: uuid(),
      isOpen: true,
      isAvailableAllDay: true,
      timeRanges: [],
    };
  });

  return {
    _id: uuid(),
    allGroupRoles: true,
    groupRole: null,
    weekTimeRules,
    isAvailableAnytime: true,
  };
}

export function getHoursOfOperationByWeeklyAvailabilities(
  availabilities: TimeAvailabilityFeatureProperties['availabilities'],
  userRoleBase62Ids: string[],
  referenceDateTime: DateTime
) {
  const fallbackAvailability = createFallbackDefaultAvailability();

  if (!availabilities || availabilities.length === 0) {
    return mapWeeklyAvailabilitiesToOperatingHours(
      [fallbackAvailability],
      referenceDateTime
    );
  }

  const userWeeklyAvailabilities = getWeeklyAvailabilitiesByMembership(
    availabilities,
    userRoleBase62Ids
  );

  if (userWeeklyAvailabilities.length) {
    return mapWeeklyAvailabilitiesToOperatingHours(
      userWeeklyAvailabilities,
      referenceDateTime
    );
  }

  const baseWeeklyAvailability = availabilities.find(wa => wa.allGroupRoles);

  const availabilityToUse = baseWeeklyAvailability || fallbackAvailability;

  if (!availabilityToUse) {
    throw new Error(
      'Bad TimeAvailability configuration, perhaps try republishing.'
    );
  }

  return mapWeeklyAvailabilitiesToOperatingHours(
    [availabilityToUse],
    referenceDateTime
  );
}
